"use client";

import { getPaymentTheme } from "src/lib/stripe-checkout/themes";
import PageContainer from "./components/PageContainer";

import type {
  StripeCustomerID,
  StripeSubscriptionID,
} from "@repo/db/branded-types";
import { STATSIG_EVENTS, useStatsigClient } from "@repo/statsig";
import {
  EmbeddedCheckoutProvider,
  type PlanDetails,
} from "@repo/stripe/context/EmbeddedCheckoutContext";
import { normalizeProductKey } from "@repo/stripe/lib/normalize-product";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ConversionDrawer } from "src/app/paywall/two-prod/components";
import {
  usePricingPlanKeys,
  type PricingPlanKeys,
} from "src/lib/pricing-plan-keys";
import PRODUCT_NAME from "src/lib/product-name";
import { StripeCheckoutContent } from "src/lib/stripe-checkout";

const paymentPageTheme = getPaymentTheme();

interface PaymentCompleteData {
  customerId: StripeCustomerID;
  subscriptionId: StripeSubscriptionID;
  isTrialing?: boolean;
}

/**
 * CheckoutContent - Renders the Stripe Elements Checkout and ConversionDrawer.
 * Hoisted outside PaymentPage so that it has stable identity and does not remount
 * when PaymentPage state (e.g. isConversionDrawerOpen) changes.
 */
type CheckoutContentProps = {
  productKey: PricingPlanKeys;
  isConversionDrawerOpen: boolean;
  onContinue: () => void;
  onPaymentComplete: (data: PaymentCompleteData) => void;
  onBack: () => void;
  backLabel?: string;
  successPath?: string;
};

const CheckoutContent = ({
  productKey,
  isConversionDrawerOpen,
  onContinue,
  onPaymentComplete,
  onBack,
  backLabel,
  successPath,
}: CheckoutContentProps) => {
  return (
    <PageContainer onBack={onBack} backLabel={backLabel}>
      <StripeCheckoutContent
        productKey={productKey}
        theme={paymentPageTheme}
        source="payment_page"
        onPaymentComplete={onPaymentComplete}
        successPath={successPath}
      />
      <ConversionDrawer
        isOpen={isConversionDrawerOpen}
        onClose={() => null}
        onContinueWithRx={onContinue}
        rxProductTitle="Fast Prescriptions"
        pedsProductTitle="Comprehensive Kids Care"
      />
    </PageContainer>
  );
};

/**
 * Payment Page - Renders the Stripe Elements checkout flow.
 *
 * Uses EmbeddedCheckoutProvider to fetch pricing plans and manage plan
 * selection state (monthly/yearly cadence, trial days), then renders
 * StripeCheckoutContent which collects payment details via Stripe Elements.
 */
const PaymentPage = ({
  productKey: productKeyProp,
  cadence: cadenceProp,
  onBack,
  backLabel,
  onPaymentComplete,
  successPath,
  initialPlanData,
}: {
  productKey?: string;
  cadence?: string;
  onBack: () => void;
  backLabel?: string;
  onPaymentComplete: (data: PaymentCompleteData) => void;
  /** Express-checkout return URL. Defaults to /home in StripeCheckoutContent;
   *  pass the originating path when checkout is rendered outside /home. */
  successPath?: string;
  /** SSR plans — when set, skips the EmbeddedCheckoutProvider client fetch. */
  initialPlanData?: { monthly: PlanDetails; yearly: PlanDetails };
}) => {
  const searchParam = useSearchParams();
  const { logEvent } = useStatsigClient();

  // Props take priority over URL search params
  const productKey =
    (productKeyProp as PricingPlanKeys) ||
    (searchParam.get("productkey") as PricingPlanKeys) ||
    "unlimitedRx";
  const intercept = searchParam.get("intercept") || "false";
  const cadence =
    ((cadenceProp as "monthly" | "yearly") ||
      (searchParam.get("cadence") as "monthly" | "yearly")) ??
    "monthly";

  const [isConversionDrawerOpen, setIsConversionDrawerOpen] = useState(
    intercept === "true"
  );

  // Map product keys to taxonomy-compliant values via the canonical
  // normalize helper so client and webhook agree.
  const taxonomyProductKey = normalizeProductKey(productKey);
  const taxonomyBillingPeriod = cadence === "yearly" ? "annual" : "monthly";

  // Track payment_submitted within this checkout session via a ref so the
  // unmount cleanup can decide whether to fire checkout_abandoned. (We use
  // a ref instead of state so updating it doesn't trigger a re-render.)
  const paymentSubmittedRef = useRef(false);
  const checkoutMountedAtRef = useRef(Date.now());

  // Log checkout_viewed event when the page loads. On unmount, fire
  // checkout_abandoned IF the user navigated away without submitting
  // payment in this session. Mobile webview close is unreliable so this
  // undercounts; useful for relative comparison across variants, not
  // absolute drop-off rate.
  useEffect(() => {
    logEvent({
      eventName: STATSIG_EVENTS.CHECKOUT_VIEWED,
      value: "embedded",
      metadata: {
        checkout_type: "embedded",
        product_key: taxonomyProductKey,
        billing_period: taxonomyBillingPeriod,
      },
    });

    // Listen for the global payment_submitted event from the inner Stripe
    // forms — they fire it on the Statsig client, but we need a local
    // signal too. Subscribe to the Statsig event stream is overkill; we
    // pass paymentSubmittedRef into the form via context — simpler: use
    // window-scoped flag keyed on this checkout session.
    const sessionKey = `__payment_submitted_${checkoutMountedAtRef.current}`;
    const handlePaymentSubmitted = () => {
      paymentSubmittedRef.current = true;
      try {
        window.sessionStorage.setItem(sessionKey, "1");
      } catch {
        // ignore — best effort
      }
    };
    window.addEventListener(
      "statsig:payment_submitted",
      handlePaymentSubmitted
    );

    return () => {
      window.removeEventListener(
        "statsig:payment_submitted",
        handlePaymentSubmitted
      );
      const submitted =
        paymentSubmittedRef.current ||
        (() => {
          try {
            return window.sessionStorage.getItem(sessionKey) === "1";
          } catch {
            return false;
          }
        })();
      if (!submitted) {
        logEvent({
          eventName: STATSIG_EVENTS.CHECKOUT_ABANDONED,
          metadata: {
            time_on_checkout_ms: String(
              Date.now() - checkoutMountedAtRef.current
            ),
            product_key: taxonomyProductKey,
            billing_period: taxonomyBillingPeriod,
          },
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only;
    // taxonomy fields snapshot at mount are fine for the abandon event.
  }, []);

  // Get pricing plan keys based on experiment group
  const pricingPlanKeys = usePricingPlanKeys();

  // Trial length comes from Stripe Price metadata.trialDays for ALL products —
  // not from the free_trial Statsig experiment. EmbeddedCheckoutProvider reads
  // it via fetchPricingPlanByKey when no override is passed, so we pass none.

  const selectedPlanKeys =
    pricingPlanKeys[productKey] || pricingPlanKeys["unlimitedRx"];

  // Build a stable key for the EmbeddedCheckoutProvider.
  const providerKey = productKey;

  const handleContinue = () => {
    setIsConversionDrawerOpen(false);
  };

  if (!selectedPlanKeys) {
    return (
      <PageContainer onBack={onBack} backLabel={backLabel}>
        <div className="text-center py-8">
          <p className="text-foreground-muted">Loading pricing plans...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <EmbeddedCheckoutProvider
      key={providerKey}
      pricingPlanKeys={selectedPlanKeys}
      productName={PRODUCT_NAME}
      defaultCadence={cadence}
      initialPlanData={initialPlanData}
    >
      <CheckoutContent
        productKey={productKey}
        isConversionDrawerOpen={isConversionDrawerOpen}
        onContinue={handleContinue}
        onPaymentComplete={onPaymentComplete}
        onBack={onBack}
        backLabel={backLabel}
        successPath={successPath}
      />
    </EmbeddedCheckoutProvider>
  );
};

export default PaymentPage;
